/**
 * PolygonRenderer3D
 *
 * Handles 3D visualization of polygons in the scene.
 * Converts 2D polygons to 3D meshes, lines, and points with proper jitter prevention.
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { Viewer } from '../../../../viewer/viewer';
import { Polygon as ProcessedPolygon, LinePromptDebugData, PixelAlignmentDebugData } from '../../../../utils/PolygonProcessor';
import { CapturedImage } from '../../../../utils/segmentation/ISegmentationStrategy';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';
import type { WorldCorner } from '../line-detection/LineDetectionWorkerManager';

/** Brand purple (French Violet) - used for segmentation preview lines and vertices */
const LINE_COLOR = 0x854dd6;

export class PolygonRenderer3D {
  private viewer: Viewer;
  private coordinateTransformer: CoordinateTransformer;

  // 3D polygon rendering
  private polygonMeshes: THREE.Mesh[] = [];
  private polygonLines: Line2[] = [];
  private polygonPoints: THREE.Points[] = [];
  private polygonContainers: THREE.Group[] = []; // Containers using local coordinates to prevent jitter

  // Overlay mesh (darkened area outside workspace)
  private overlayMesh: THREE.Mesh | null = null;

  // Preview mesh (highlighted region on hover)
  private previewMesh: THREE.Mesh | null = null;
  private previewCanvas: HTMLCanvasElement | null = null;
  private previewTexture: THREE.CanvasTexture | null = null;
  private currentPreviewHash: string | null = null;

  // Debug rendering
  private debugLines: Line2[] = [];
  private debugPoints: THREE.Points[] = [];

  constructor(viewer: Viewer, coordinateTransformer: CoordinateTransformer) {
    this.viewer = viewer;
    this.coordinateTransformer = coordinateTransformer;
  }

  /**
   * Convert pixel coordinates to world space using provided corners
   * This allows rendering with historical corners when the camera has moved
   */
  private pixelToWorldWithCorners(pixelX: number, pixelY: number, imageWidth: number, imageHeight: number, corners: THREE.Vector3[]): THREE.Vector3 | null {
    if (corners.length !== 4) return null;

    // Convert from pixel space to normalized [0,1]
    const u = pixelX / imageWidth;
    const v = pixelY / imageHeight;

    // Bilinear interpolation using provided corners
    const [topLeft, topRight, bottomRight, bottomLeft] = corners;

    const top = new THREE.Vector3().lerpVectors(topLeft, topRight, u);
    const bottom = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u);
    return new THREE.Vector3().lerpVectors(top, bottom, v);
  }

  /**
   * Render polygons in 3D space
   * @param polygons - Array of polygons to render
   * @param capturedImage - The captured image (for dimensions)
   * @param worldCorners - Optional world-space corners to use for positioning (if not provided, uses current transformer corners)
   */
  renderPolygons(polygons: ProcessedPolygon[], capturedImage: CapturedImage | null, worldCorners?: THREE.Vector3[] | WorldCorner[] | null): void {
    // Convert plain objects to THREE.Vector3 if needed
    let maskCorners: THREE.Vector3[];
    if (worldCorners && worldCorners.length === 4) {
      maskCorners = worldCorners.map((c) => (c instanceof THREE.Vector3 ? c : new THREE.Vector3(c.x, c.y, c.z)));
    } else {
      maskCorners = this.coordinateTransformer.getMaskCorners();
    }
    if (polygons.length === 0 || !maskCorners || maskCorners.length === 0) return;

    // Get image dimensions for pixel-to-world conversion
    const imageWidth = capturedImage?.width ?? this.coordinateTransformer['capturedImage']?.width ?? 0;
    const imageHeight = capturedImage?.height ?? this.coordinateTransformer['capturedImage']?.height ?? 0;
    if (imageWidth === 0 || imageHeight === 0) return;

    // Clear old meshes/lines
    this.clearPolygonRendering();

    // Helper to convert pixel to world using provided corners
    const pixelToWorld = (x: number, y: number): THREE.Vector3 | null => {
      if (worldCorners) {
        return this.pixelToWorldWithCorners(x, y, imageWidth, imageHeight, maskCorners);
      }
      return this.coordinateTransformer.pixelToWorld(x, y);
    };

    // Convert mask space polygons (pixel space) to 3D world space during rendering
    for (const polygon of polygons) {
      if (polygon.points.length < 3) {
        continue;
      }

      // Create 3D vertices by converting from pixel space to world space
      const vertices: THREE.Vector3[] = [];

      for (const point of polygon.points) {
        const worldPos = pixelToWorld(point.x, point.y);
        if (worldPos) {
          vertices.push(worldPos);
        }
      }

      if (vertices.length < 3) continue;

      // Create geometry from vertices (triangulate polygon)
      const origin = vertices[0];

      // Find first edge vector
      const edge1 = new THREE.Vector3().subVectors(vertices[1], origin).normalize();

      // Find a good second edge that's not collinear with the first
      let edge2: THREE.Vector3 | null = null;
      for (let i = 2; i < vertices.length; i++) {
        const candidate = new THREE.Vector3().subVectors(vertices[i], origin);
        const cross = new THREE.Vector3().crossVectors(edge1, candidate);
        if (cross.length() > 0.001) {
          edge2 = cross.cross(edge1).normalize();
          break;
        }
      }

      // Fallback if all points are collinear
      if (!edge2) {
        edge2 = new THREE.Vector3(0, 0, 1);
        if (Math.abs(edge1.dot(edge2)) > 0.9) {
          edge2 = new THREE.Vector3(0, 1, 0);
        }
        edge2.crossVectors(edge1, edge2).normalize();
      }

      // Project all vertices onto this 2D coordinate system
      const localVertices = vertices.map((v) => {
        const diff = new THREE.Vector3().subVectors(v, origin);
        return new THREE.Vector2(diff.dot(edge1), diff.dot(edge2));
      });

      // Create shape from 2D projected vertices
      const shape = new THREE.Shape();
      if (localVertices.length > 0) {
        shape.moveTo(localVertices[0].x, localVertices[0].y);
        for (let i = 1; i < localVertices.length; i++) {
          shape.lineTo(localVertices[i].x, localVertices[i].y);
        }
        shape.closePath();
      }

      // Add holes from children
      if (polygon.children && polygon.children.length > 0) {
        for (const hole of polygon.children) {
          if (hole.points.length < 3) continue;

          // Convert hole vertices to 3D world space
          const holeVertices3D: THREE.Vector3[] = [];
          for (const point of hole.points) {
            const worldPos = pixelToWorld(point.x, point.y);
            if (worldPos) {
              holeVertices3D.push(worldPos);
            }
          }

          if (holeVertices3D.length < 3) continue;

          // Project hole vertices to local 2D coordinates
          const holeLocalVertices = holeVertices3D.map((v) => {
            const diff = new THREE.Vector3().subVectors(v, origin);
            return new THREE.Vector2(diff.dot(edge1), diff.dot(edge2));
          });

          // Create hole path
          const holePath = new THREE.Path();
          if (holeLocalVertices.length > 0) {
            holePath.moveTo(holeLocalVertices[0].x, holeLocalVertices[0].y);
            for (let i = 1; i < holeLocalVertices.length; i++) {
              holePath.lineTo(holeLocalVertices[i].x, holeLocalVertices[i].y);
            }
            holePath.closePath();
          }

          shape.holes.push(holePath);
        }
      }

      const geometry = new THREE.ShapeGeometry(shape);

      // CRITICAL: Use local coordinate system to prevent jitter at high elevations (400m-800m)
      // Convert the triangulated geometry to LOCAL coordinates (relative to origin)
      const positions = geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < positions.length; i += 3) {
        const localX = positions[i];
        const localY = positions[i + 1];

        // Reconstruct 3D position from local coordinates
        const worldPos = origin.clone().add(edge1.clone().multiplyScalar(localX)).add(edge2.clone().multiplyScalar(localY));

        // Convert to local coordinates relative to origin
        const localPos = new THREE.Vector3().subVectors(worldPos, origin);
        positions[i] = localPos.x;
        positions[i + 1] = localPos.y;
        positions[i + 2] = localPos.z;
      }

      geometry.attributes.position.needsUpdate = true;
      geometry.computeVertexNormals();

      // Position the container at the first vertex (origin), similar to Measure.ts pattern
      const polygonContainer = new THREE.Group();
      polygonContainer.position.copy(origin);

      // Create semi-transparent mesh (geometry now uses local coords)
      const material = new THREE.MeshBasicMaterial({
        color: 0x59cd90,
        transparent: true,
        opacity: 0.3,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.renderOrder = 999;
      polygonContainer.add(mesh);
      this.viewer.scene.scene.add(polygonContainer);
      this.polygonMeshes.push(mesh);
      this.polygonContainers.push(polygonContainer); // Store container for cleanup

      // Create outline lines using Line2 with LOCAL coordinates
      const linePositions: number[] = [];
      for (let i = 0; i <= vertices.length; i++) {
        const v = vertices[i % vertices.length];
        // Convert to local coordinates relative to origin
        const localV = new THREE.Vector3().subVectors(v, origin);
        linePositions.push(localV.x, localV.y, localV.z);
      }

      const lineGeometry = new LineGeometry();
      lineGeometry.setPositions(linePositions);

      const lineMaterial = new LineMaterial({
        color: LINE_COLOR,
        linewidth: 3,
        resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
        depthTest: false,
      });

      const line = new Line2(lineGeometry, lineMaterial);
      line.renderOrder = 1000;
      line.computeLineDistances();
      polygonContainer.add(line); // Add to container, not scene
      this.polygonLines.push(line);

      // Create vertex points with LOCAL coordinates
      const pointsGeometry = new THREE.BufferGeometry();
      const pointPositions = new Float32Array(vertices.length * 3);
      vertices.forEach((v, i) => {
        const localV = new THREE.Vector3().subVectors(v, origin);
        pointPositions[i * 3] = localV.x;
        pointPositions[i * 3 + 1] = localV.y;
        pointPositions[i * 3 + 2] = localV.z;
      });
      pointsGeometry.setAttribute('position', new THREE.BufferAttribute(pointPositions, 3));

      const pointsMaterial = new THREE.PointsMaterial({
        color: LINE_COLOR,
        size: 8,
        sizeAttenuation: false,
        depthTest: false,
      });

      const points = new THREE.Points(pointsGeometry, pointsMaterial);
      points.renderOrder = 1001;
      polygonContainer.add(points); // Add to container, not scene
      this.polygonPoints.push(points);

      // Render hole outlines and vertices
      if (polygon.children && polygon.children.length > 0) {
        for (const hole of polygon.children) {
          if (!hole.isHole || hole.points.length < 3) continue;

          // Convert hole vertices to 3D world space
          const holeVertices: THREE.Vector3[] = [];
          for (const point of hole.points) {
            const worldPos = this.coordinateTransformer.pixelToWorld(point.x, point.y);
            if (worldPos) {
              holeVertices.push(worldPos);
            }
          }

          if (holeVertices.length < 3) continue;

          // Create hole outline with LOCAL coordinates
          const holeLinePositions: number[] = [];
          for (let i = 0; i <= holeVertices.length; i++) {
            const v = holeVertices[i % holeVertices.length];
            const localV = new THREE.Vector3().subVectors(v, origin);
            holeLinePositions.push(localV.x, localV.y, localV.z);
          }

          const holeLineGeometry = new LineGeometry();
          holeLineGeometry.setPositions(holeLinePositions);

          const holeLineMaterial = new LineMaterial({
            color: 0xff0000, // Red color for holes
            linewidth: 3,
            resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
            depthTest: false,
          });

          const holeLine = new Line2(holeLineGeometry, holeLineMaterial);
          holeLine.renderOrder = 1002;
          holeLine.computeLineDistances();
          polygonContainer.add(holeLine);
          this.polygonLines.push(holeLine);

          // Create hole vertex points with LOCAL coordinates
          const holePointsGeometry = new THREE.BufferGeometry();
          const holePointPositions = new Float32Array(holeVertices.length * 3);
          holeVertices.forEach((v, i) => {
            const localV = new THREE.Vector3().subVectors(v, origin);
            holePointPositions[i * 3] = localV.x;
            holePointPositions[i * 3 + 1] = localV.y;
            holePointPositions[i * 3 + 2] = localV.z;
          });
          holePointsGeometry.setAttribute('position', new THREE.BufferAttribute(holePointPositions, 3));

          const holePointsMaterial = new THREE.PointsMaterial({
            color: 0xff0000, // Red color for hole vertices
            size: 8,
            sizeAttenuation: false,
            depthTest: false,
          });

          const holePoints = new THREE.Points(holePointsGeometry, holePointsMaterial);
          holePoints.renderOrder = 1003;
          polygonContainer.add(holePoints);
          this.polygonPoints.push(holePoints);
        }
      }
    }
  }

  /**
   * Clear all polygon rendering
   */
  clearPolygonRendering(): void {
    // Remove containers (which contain meshes, lines, and points)
    this.polygonContainers.forEach((container) => {
      this.viewer.scene.scene.remove(container);
      // Dispose all children
      container.traverse((child: THREE.Object3D) => {
        if ((child as THREE.Mesh).geometry) {
          (child as THREE.Mesh).geometry.dispose();
        }
        if ((child as THREE.Mesh).material) {
          const material = (child as THREE.Mesh).material;
          if (Array.isArray(material)) {
            material.forEach((mat: THREE.Material) => mat.dispose());
          } else {
            material.dispose();
          }
        }
      });
    });
    this.polygonContainers = [];
    this.polygonMeshes = [];
    this.polygonLines = [];
    this.polygonPoints = [];

    // NOTE: Overlay mesh is NOT removed here - it should persist across polygon updates
    // It's only removed when the tool is disabled or reset
  }

  /**
   * Create a semi-transparent dark overlay mesh that covers everything except the work area
   */
  createOverlayMesh(): void {
    // Remove old overlay if exists
    this.clearOverlayMesh();

    const maskCorners = this.coordinateTransformer.getMaskCorners();
    if (maskCorners.length !== 4) return;

    // Get the workspace corners (in 3D world space)
    const [topLeft, topRight, bottomRight, bottomLeft] = maskCorners;

    // CRITICAL: Use local coordinate system to prevent jitter at high elevations
    // Position the mesh at the workspace center (average of corners)
    const center = new THREE.Vector3().add(topLeft).add(topRight).add(bottomRight).add(bottomLeft).multiplyScalar(0.25);

    // Calculate the extent of the overlay (make it very large)
    const largeSize = 10000;

    // Calculate vectors along the edges of the workspace
    const edge1 = new THREE.Vector3().subVectors(topRight, topLeft);
    const edge2 = new THREE.Vector3().subVectors(bottomLeft, topLeft);

    // Create two perpendicular vectors in the workspace plane for extending the overlay
    const right = edge1.clone().normalize();
    const down = edge2.clone().normalize();

    // Extend the corners outward to create the overlay boundary (in world space first)
    const extendDist = largeSize;
    const outerTopLeft = topLeft.clone().add(right.clone().multiplyScalar(-extendDist)).add(down.clone().multiplyScalar(-extendDist));
    const outerTopRight = topRight.clone().add(right.clone().multiplyScalar(extendDist)).add(down.clone().multiplyScalar(-extendDist));
    const outerBottomRight = bottomRight.clone().add(right.clone().multiplyScalar(extendDist)).add(down.clone().multiplyScalar(extendDist));
    const outerBottomLeft = bottomLeft.clone().add(right.clone().multiplyScalar(-extendDist)).add(down.clone().multiplyScalar(extendDist));

    // Create vertices for a plane with a hole (outer square with inner workspace cutout)
    const vertices: number[] = [];
    const indices: number[] = [];

    // Define the vertices (outer boundary + inner hole boundary) in LOCAL coordinates
    // Outer boundary: 0-3, Inner boundary (workspace): 4-7
    const allVertices = [
      outerTopLeft,
      outerTopRight,
      outerBottomRight,
      outerBottomLeft, // 0-3 outer
      topLeft,
      topRight,
      bottomRight,
      bottomLeft, // 4-7 inner (workspace)
    ];

    // Convert to local coordinates relative to center
    allVertices.forEach((v) => {
      const localV = new THREE.Vector3().subVectors(v, center);
      vertices.push(localV.x, localV.y, localV.z);
    });

    // Create triangles for the 4 strips around the workspace hole
    // Top strip
    indices.push(0, 4, 5, 0, 5, 1);
    // Right strip
    indices.push(1, 5, 6, 1, 6, 2);
    // Bottom strip
    indices.push(2, 6, 7, 2, 7, 3);
    // Left strip
    indices.push(3, 7, 4, 3, 4, 0);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    const material = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    });

    this.overlayMesh = new THREE.Mesh(geometry, material);
    this.overlayMesh.position.copy(center); // Position at center for local coordinates
    this.overlayMesh.renderOrder = 997; // Render behind polygons but in front of point cloud
    this.viewer.scene.scene.add(this.overlayMesh);
  }

  /**
   * Clear the overlay mesh
   */
  clearOverlayMesh(): void {
    if (this.overlayMesh) {
      this.viewer.scene.scene.remove(this.overlayMesh);
      this.overlayMesh.geometry.dispose();
      if (Array.isArray(this.overlayMesh.material)) {
        this.overlayMesh.material.forEach((mat) => mat.dispose());
      } else {
        this.overlayMesh.material.dispose();
      }
      this.overlayMesh = null;
    }
  }

  /**
   * Render pixel alignment debug visualization
   * @param debugData The pixel alignment debug data from PolygonProcessor
   * @param capturedImage Image dimensions for coordinate conversion (only width/height needed)
   */
  renderPixelAlignmentDebug(debugData: PixelAlignmentDebugData, capturedImage: { width: number; height: number } | null): void {
    // Clear existing debug visualizations first
    this.debugLines.forEach((line) => {
      this.viewer.scene.scene.remove(line);
      line.geometry.dispose();
      if (line.material) line.material.dispose();
    });
    this.debugLines = [];

    this.debugPoints.forEach((points) => {
      this.viewer.scene.scene.remove(points);
      points.geometry.dispose();
      if (Array.isArray(points.material)) {
        points.material.forEach((m) => m.dispose());
      } else {
        points.material.dispose();
      }
    });
    this.debugPoints = [];

    if (!debugData || !capturedImage) {
      return;
    }

    const maskCorners = this.coordinateTransformer.getMaskCorners();
    if (maskCorners.length === 0) return;

    const topLeft = maskCorners[0];
    const topRight = maskCorners[1];
    const bottomRight = maskCorners[2];
    const bottomLeft = maskCorners[3];

    // Render sample scan lines in cyan (thin)
    debugData.sampleLines.forEach((line) => {
      // Convert from pixel coordinates to normalized [0,1] then to world space
      const u1 = line.start.x / capturedImage.width;
      const v1 = line.start.y / capturedImage.height;
      const u2 = line.end.x / capturedImage.width;
      const v2 = line.end.y / capturedImage.height;

      const top1 = new THREE.Vector3().lerpVectors(topLeft, topRight, u1);
      const bottom1 = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u1);
      const worldPos1 = new THREE.Vector3().lerpVectors(top1, bottom1, v1);

      const top2 = new THREE.Vector3().lerpVectors(topLeft, topRight, u2);
      const bottom2 = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u2);
      const worldPos2 = new THREE.Vector3().lerpVectors(top2, bottom2, v2);

      const positions: number[] = [...worldPos1.toArray(), ...worldPos2.toArray()];

      const lineGeometry = new LineGeometry();
      lineGeometry.setPositions(positions);

      const lineMaterial = new LineMaterial({
        color: 0x00ffff, // Cyan
        linewidth: 1,
        resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      });

      const lineObj = new Line2(lineGeometry, lineMaterial);
      lineObj.renderOrder = 999;
      this.viewer.scene.scene.add(lineObj);
      this.debugLines.push(lineObj);
    });

    // Render peak points with color based on weight (red=low weight, green=high weight)
    if (debugData.peakPoints.length > 0) {
      const geometry = new THREE.BufferGeometry();
      const positions: number[] = [];
      const colors: number[] = [];
      const weights = debugData.peakPointWeights || [];

      debugData.peakPoints.forEach((peak, idx) => {
        // Convert from pixel coordinates to normalized [0,1] then to world space
        const u = peak.x / capturedImage.width;
        const v = peak.y / capturedImage.height;

        const top = new THREE.Vector3().lerpVectors(topLeft, topRight, u);
        const bottom = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u);
        const worldPos = new THREE.Vector3().lerpVectors(top, bottom, v);

        positions.push(...worldPos.toArray());

        // Color based on weight: red (low) -> yellow (mid) -> green (high)
        const weight = weights[idx] ?? 0.5;
        // Red to green gradient: low weight = red, high weight = green
        const r = 1 - weight;
        const g = weight;
        const b = 0;
        colors.push(r, g, b);
      });

      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

      const material = new THREE.PointsMaterial({
        size: 8,
        sizeAttenuation: false,
        depthTest: false,
        depthWrite: false,
        vertexColors: true, // Use per-vertex colors
      });

      const points = new THREE.Points(geometry, material);
      points.renderOrder = 1000;
      this.viewer.scene.scene.add(points);
      this.debugPoints.push(points);
    }

    // Render above-threshold peak regions in green
    if (debugData.peakRegions && debugData.peakRegions.length > 0) {
      debugData.peakRegions.forEach((region) => {
        const u1 = region.start.x / capturedImage.width;
        const v1 = region.start.y / capturedImage.height;
        const u2 = region.end.x / capturedImage.width;
        const v2 = region.end.y / capturedImage.height;

        const top1 = new THREE.Vector3().lerpVectors(topLeft, topRight, u1);
        const bottom1 = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u1);
        const worldPos1 = new THREE.Vector3().lerpVectors(top1, bottom1, v1);

        const top2 = new THREE.Vector3().lerpVectors(topLeft, topRight, u2);
        const bottom2 = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u2);
        const worldPos2 = new THREE.Vector3().lerpVectors(top2, bottom2, v2);

        const positions: number[] = [...worldPos1.toArray(), ...worldPos2.toArray()];

        const lineGeometry = new LineGeometry();
        lineGeometry.setPositions(positions);

        const lineMaterial = new LineMaterial({
          color: 0x00ff00, // Green
          linewidth: 2,
          resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
        });

        const lineObj = new Line2(lineGeometry, lineMaterial);
        lineObj.renderOrder = 999;
        this.viewer.scene.scene.add(lineObj);
        this.debugLines.push(lineObj);
      });
    }

    // Render sampled position map points in magenta
    if (debugData.sampledPositionPoints && debugData.sampledPositionPoints.length > 0) {
      const geometry = new THREE.BufferGeometry();
      const positions: number[] = [];

      debugData.sampledPositionPoints.forEach((pt) => {
        const u = pt.x / capturedImage.width;
        const v = pt.y / capturedImage.height;

        const top = new THREE.Vector3().lerpVectors(topLeft, topRight, u);
        const bottom = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u);
        const worldPos = new THREE.Vector3().lerpVectors(top, bottom, v);

        positions.push(...worldPos.toArray());
      });

      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

      const material = new THREE.PointsMaterial({
        color: 0xff00ff, // Magenta
        size: 6,
        sizeAttenuation: false,
        depthTest: false,
        depthWrite: false,
      });

      const points = new THREE.Points(geometry, material);
      points.renderOrder = 1000;
      this.viewer.scene.scene.add(points);
      this.debugPoints.push(points);
    }

    // Render fitted lines in red (thick)
    debugData.fittedLines.forEach((line) => {
      // Convert from pixel coordinates to normalized [0,1] then to world space
      const u1 = line.start.x / capturedImage.width;
      const v1 = line.start.y / capturedImage.height;
      const u2 = line.end.x / capturedImage.width;
      const v2 = line.end.y / capturedImage.height;

      const top1 = new THREE.Vector3().lerpVectors(topLeft, topRight, u1);
      const bottom1 = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u1);
      const worldPos1 = new THREE.Vector3().lerpVectors(top1, bottom1, v1);

      const top2 = new THREE.Vector3().lerpVectors(topLeft, topRight, u2);
      const bottom2 = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u2);
      const worldPos2 = new THREE.Vector3().lerpVectors(top2, bottom2, v2);

      const positions: number[] = [...worldPos1.toArray(), ...worldPos2.toArray()];

      const lineGeometry = new LineGeometry();
      lineGeometry.setPositions(positions);

      const lineMaterial = new LineMaterial({
        color: 0xff0000, // Red
        linewidth: 3,
        resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      });

      const lineObj = new Line2(lineGeometry, lineMaterial);
      lineObj.renderOrder = 1000;
      this.viewer.scene.scene.add(lineObj);
      this.debugLines.push(lineObj);
    });
  }

  /**
   * Render line prompt edge adjustment debug visualization.
   * Each category uses distinct colors for easy identification.
   */
  renderLinePromptDebug(debugData: LinePromptDebugData | null, capturedImage: CapturedImage | null): void {
    // Clear existing debug visualizations first
    this.clearDebugVisualization();

    if (!debugData || !capturedImage) {
      return;
    }

    const maskCorners = this.coordinateTransformer.getMaskCorners();
    if (maskCorners.length === 0) return;

    const topLeft = maskCorners[0];
    const topRight = maskCorners[1];
    const bottomRight = maskCorners[2];
    const bottomLeft = maskCorners[3];

    const toWorld = (px: { x: number; y: number }): THREE.Vector3 => {
      const u = px.x / capturedImage.width;
      const v = px.y / capturedImage.height;
      const top = new THREE.Vector3().lerpVectors(topLeft, topRight, u);
      const bottom = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u);
      return new THREE.Vector3().lerpVectors(top, bottom, v);
    };

    const addLine = (start: { x: number; y: number }, end: { x: number; y: number }, color: number, linewidth: number, order: number) => {
      const w1 = toWorld(start);
      const w2 = toWorld(end);
      const lineGeometry = new LineGeometry();
      lineGeometry.setPositions([...w1.toArray(), ...w2.toArray()]);
      const lineMaterial = new LineMaterial({
        color,
        linewidth,
        resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      });
      const lineObj = new Line2(lineGeometry, lineMaterial);
      lineObj.renderOrder = order;
      this.viewer.scene.scene.add(lineObj);
      this.debugLines.push(lineObj);
    };

    const addPoints = (pts: { x: number; y: number }[], color: number, size: number, order: number) => {
      if (pts.length === 0) return;
      const geometry = new THREE.BufferGeometry();
      const positions: number[] = [];
      for (const pt of pts) {
        positions.push(...toWorld(pt).toArray());
      }
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      const material = new THREE.PointsMaterial({
        color,
        size,
        sizeAttenuation: false,
        depthTest: false,
        depthWrite: false,
      });
      const points = new THREE.Points(geometry, material);
      points.renderOrder = order;
      this.viewer.scene.scene.add(points);
      this.debugPoints.push(points);
    };

    // 1. Rays + hit points — cyan lines, yellow dots
    for (const ray of debugData.rays) {
      addLine(ray.start, ray.end, 0x00ffff, 1, 998);
    }
    addPoints(debugData.rayHits, 0xffff00, 5, 999);

    // 2. Candidate edges — orange
    for (const edge of debugData.candidateEdges) {
      addLine(edge.start, edge.end, 0xff8800, 4, 999);
    }

    // 3. All chains — non-selected in gray, selected in green
    for (const chain of debugData.allChains) {
      const color = chain.selected ? 0x00ff00 : 0x888888;
      const width = chain.selected ? 6 : 3;
      for (const edge of chain.edges) {
        addLine(edge.start, edge.end, color, width, chain.selected ? 1001 : 997);
      }
    }

    // 4. Selected chain original — green
    for (const edge of debugData.selectedChainOriginal) {
      addLine(edge.start, edge.end, 0x00ff00, 6, 1001);
    }

    // 5. Collapsed edge — magenta
    if (debugData.collapsedEdge) {
      addLine(debugData.collapsedEdge.start, debugData.collapsedEdge.end, 0xff00ff, 6, 1002);
    }

    // 6. Seed edge — red, thickest so it's visible underneath
    if (debugData.seedEdge) {
      addLine(debugData.seedEdge.start, debugData.seedEdge.end, 0xff0000, 8, 996);
    }

    // 7. Start outer edges — dark blue, thin (edges near the prompt line start point)
    for (const edge of debugData.startOuterEdges) {
      addLine(edge.start, edge.end, 0x4444cc, 8, 995);
    }

    // 8. End outer edges — cyan, thin (edges near the prompt line end point)
    for (const edge of debugData.endOuterEdges) {
      addLine(edge.start, edge.end, 0x44cccc, 8, 995);
    }
  }

  /**
   * Clear debug visualization
   */
  clearDebugVisualization(): void {
    this.debugLines.forEach((line) => {
      this.viewer.scene.scene.remove(line);
      line.geometry.dispose();
      if (line.material) line.material.dispose();
    });
    this.debugLines = [];

    this.debugPoints.forEach((points) => {
      this.viewer.scene.scene.remove(points);
      points.geometry.dispose();
      if (Array.isArray(points.material)) {
        points.material.forEach((m) => m.dispose());
      } else {
        points.material.dispose();
      }
    });
    this.debugPoints = [];
  }

  /**
   * Compute a simple hash of the mask to detect region changes
   */
  private computeMaskHash(mask: ImageData): string {
    // Sample every 100th pixel for fast hashing
    let hash = 0;
    for (let i = 0; i < mask.data.length; i += 400) {
      hash = ((hash << 5) - hash + mask.data[i]) | 0;
    }
    return `${mask.width}x${mask.height}:${hash}`;
  }

  /**
   * Render a preview mask with the brand green color
   * Used for hover preview to show which region would be selected
   *
   * @param previewMask - The mask ImageData (white pixels = region)
   * @param worldCorners - Optional world-space corners to use for positioning (if not provided, uses current transformer corners)
   */
  renderPreviewMask(previewMask: ImageData | null, worldCorners?: THREE.Vector3[] | WorldCorner[] | null): void {
    if (!previewMask) {
      this.clearPreviewMesh();
      return;
    }

    // Check if this is the same region (no need to re-render or fade)
    const newHash = this.computeMaskHash(previewMask);
    if (newHash === this.currentPreviewHash && this.previewMesh) {
      return; // Same region, keep current mesh
    }

    this.currentPreviewHash = newHash;

    // Clear existing preview mesh (but keep animation state if transitioning)
    if (this.previewMesh) {
      this.viewer.scene.scene.remove(this.previewMesh);
      this.previewMesh.geometry.dispose();
      if (Array.isArray(this.previewMesh.material)) {
        this.previewMesh.material.forEach((mat) => mat.dispose());
      } else {
        this.previewMesh.material.dispose();
      }
      this.previewMesh = null;
    }

    // Use provided world corners or fall back to current transformer corners
    // Convert plain objects to THREE.Vector3 if needed
    let maskCorners: THREE.Vector3[];
    if (worldCorners && worldCorners.length === 4) {
      maskCorners = worldCorners.map((c) => (c instanceof THREE.Vector3 ? c : new THREE.Vector3(c.x, c.y, c.z)));
    } else {
      maskCorners = this.coordinateTransformer.getMaskCorners();
    }
    if (!maskCorners || maskCorners.length !== 4) return;

    const [topLeft, topRight, bottomRight, bottomLeft] = maskCorners;

    // Create or reuse canvas for texture
    if (!this.previewCanvas) {
      this.previewCanvas = document.createElement('canvas');
    }

    this.previewCanvas.width = previewMask.width;
    this.previewCanvas.height = previewMask.height;

    const ctx = this.previewCanvas.getContext('2d');
    if (!ctx) return;

    // Create a new ImageData with brand neon-green color for the mask
    // neon-green-500: #7EDD64 = rgb(126, 221, 100)
    const coloredMask = ctx.createImageData(previewMask.width, previewMask.height);
    for (let i = 0; i < previewMask.data.length; i += 4) {
      if (previewMask.data[i] > 0) {
        // White pixel in mask - color it neon-green with semi-transparency
        coloredMask.data[i] = 126; // R
        coloredMask.data[i + 1] = 221; // G
        coloredMask.data[i + 2] = 100; // B
        coloredMask.data[i + 3] = 128; // A (semi-transparent)
      } else {
        // Transparent
        coloredMask.data[i] = 0;
        coloredMask.data[i + 1] = 0;
        coloredMask.data[i + 2] = 0;
        coloredMask.data[i + 3] = 0;
      }
    }

    ctx.putImageData(coloredMask, 0, 0);

    // Create or update texture
    if (this.previewTexture) {
      this.previewTexture.dispose();
    }
    this.previewTexture = new THREE.CanvasTexture(this.previewCanvas);
    this.previewTexture.needsUpdate = true;

    // Calculate center of the workspace for local coordinates
    const center = new THREE.Vector3().add(topLeft).add(topRight).add(bottomRight).add(bottomLeft).multiplyScalar(0.25);

    // Create a plane geometry that matches the workspace
    const localTopLeft = new THREE.Vector3().subVectors(topLeft, center);
    const localTopRight = new THREE.Vector3().subVectors(topRight, center);
    const localBottomRight = new THREE.Vector3().subVectors(bottomRight, center);
    const localBottomLeft = new THREE.Vector3().subVectors(bottomLeft, center);

    const vertices = new Float32Array([
      localTopLeft.x,
      localTopLeft.y,
      localTopLeft.z,
      localTopRight.x,
      localTopRight.y,
      localTopRight.z,
      localBottomRight.x,
      localBottomRight.y,
      localBottomRight.z,
      localBottomLeft.x,
      localBottomLeft.y,
      localBottomLeft.z,
    ]);

    // UV coordinates - Y is flipped because image data has origin at top-left
    const uvs = new Float32Array([0, 1, 1, 1, 1, 0, 0, 0]);

    const indices = new Uint16Array([0, 2, 1, 0, 3, 2]);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    const material = new THREE.MeshBasicMaterial({
      map: this.previewTexture,
      transparent: true,
      opacity: 1, // Instant display, no fade-in
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    });

    this.previewMesh = new THREE.Mesh(geometry, material);
    this.previewMesh.position.copy(center);
    this.previewMesh.renderOrder = 998; // Between overlay (997) and polygons (999)
    this.viewer.scene.scene.add(this.previewMesh);
  }

  /**
   * Returns true if a preview mask mesh is currently rendered.
   */
  hasPreviewMesh(): boolean {
    return this.previewMesh !== null;
  }

  /**
   * Clear the preview mesh
   */
  clearPreviewMesh(): void {
    // Reset hash so next hover will show correctly
    this.currentPreviewHash = null;

    if (this.previewMesh) {
      this.viewer.scene.scene.remove(this.previewMesh);
      this.previewMesh.geometry.dispose();
      if (Array.isArray(this.previewMesh.material)) {
        this.previewMesh.material.forEach((mat) => mat.dispose());
      } else {
        this.previewMesh.material.dispose();
      }
      this.previewMesh = null;
    }
  }

  /**
   * Dispose and clean up all resources
   */
  dispose(): void {
    this.clearPolygonRendering();
    this.clearOverlayMesh();
    this.clearPreviewMesh();
    this.clearDebugVisualization();

    if (this.previewTexture) {
      this.previewTexture.dispose();
      this.previewTexture = null;
    }
    this.previewCanvas = null;
  }
}
